import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

// Define the structure for the specific query parameters we want to capture
interface CapturedQueryParams {
  utm_campaign: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  af: string | null;
}

// Define the context type
interface QueryParamsContextType {
  params: CapturedQueryParams;
}

const QueryParamsContext = createContext<QueryParamsContextType | undefined>(
  undefined,
);

// List of query parameters to capture
const TARGET_QUERY_PARAMS: (keyof CapturedQueryParams)[] = [
  "utm_campaign",
  "utm_source",
  "utm_medium",
  "af",
];

export const QueryParamsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const [capturedParams, setCapturedParams] = useState<CapturedQueryParams>({
    utm_campaign: null,
    utm_source: null,
    utm_medium: null,
    af: null,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const newCapturedParams: Partial<CapturedQueryParams> = {}; // Use Partial for building the object

    TARGET_QUERY_PARAMS.forEach((paramKey) => {
      newCapturedParams[paramKey] = searchParams.get(paramKey);
    });

    setCapturedParams(newCapturedParams as CapturedQueryParams); // Cast to full type once all keys are set
  }, []); // Capture only on mount and update

  return (
    <QueryParamsContext.Provider value={{ params: capturedParams }}>
      {children}
    </QueryParamsContext.Provider>
  );
};

export const useQueryParams = (): CapturedQueryParams => {
  const context = useContext(QueryParamsContext);
  if (context === undefined) {
    throw new Error("useQueryParams must be used within a QueryParamsProvider");
  }
  return context.params;
};
